import React, { useEffect } from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { SignupModalProvider } from './context/SignupModalContext';
import { ZoneSelectionModal } from './components/SimulationMiniCart';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import DynamicPage from './pages/DynamicPage';
import FavorisPage from './pages/FavorisPage';
import ProductDetailPage from './pages/ProductDetailPage';
import ProfilePage from './pages/ProfilePage';
import PersonalSpacePage from './pages/PersonalSpacePage';
import PublicPage from './pages/PublicPage';
import BlogPostPage from './pages/BlogPostPage';
import OfferDetailPage from './pages/OfferDetailPage';
import PartnerPage from './pages/PartnerPage';
import PartnerCatalogPage from './pages/PartnerCatalogPage';
import PartnersListPage from './pages/PartnersListPage';
// NouveautesPage supprimé - remplacé par HubPage (voir AUDIT_PAGES_COMPLET.md)
import HubPage from './pages/HubPage';
import ListingPage from './pages/ListingPage';
import SimulationCartPage from './pages/SimulationCartPage';
import PurchaseOrderDetailPage from './pages/PurchaseOrderDetailPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminPartners from './pages/admin/AdminPartners';
import AdminPages from './pages/admin/AdminPages';
import AdminUsers from './pages/admin/AdminUsers';
import AdminEditUser from './pages/admin/AdminEditUser';
import AdminPublications from './pages/admin/AdminPublications';
import AdminComments from './pages/admin/AdminComments';
import AdminPageBlocks from './pages/admin/AdminPageBlocks';
import AdminGlobalBlocks from './pages/admin/AdminGlobalBlocks';
import AdminAudit from './pages/admin/AdminAudit';
import AdminOffers from './pages/admin/AdminOffers';
import AdminVitrines from './pages/admin/AdminVitrines';
import AdminImportV2 from './pages/admin/AdminImportV2';
import AdminReferentiels from './pages/admin/AdminReferentiels';
import AdminOrganizations from './pages/admin/AdminOrganizations';
import PartnerDashboard from './pages/partner/PartnerDashboard';
import PartnerProducts from './pages/partner/PartnerProducts';
import PartnerOffers from './pages/partner/PartnerOffers';
import PartnerEntreprise from './pages/partner/PartnerEntreprise';
import PartnerOrders from './pages/partner/PartnerOrders';
import PartnerOrderDetail from './pages/partner/PartnerOrderDetail';
import AdminInvitations from './pages/admin/AdminInvitations';
import InvitationPage from './pages/InvitationPage';
import SearchPage from './pages/SearchPage';
import OfferPreviewSandbox from './pages/OfferPreviewSandbox';
import PartnerPreviewSandbox from './pages/PartnerPreviewSandbox';

// Redirect component based on user role
const RoleBasedDashboard = () => {
  const { user } = useAuth();
  
  if (user?.role === 'admin') {
    return <Navigate to="/dashboard/admin" replace />;
  } else if (user?.role === 'partenaire') {
    return <Navigate to="/dashboard/partner" replace />;
  }
  
  return <DashboardPage />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <SignupModalProvider>
        <CartProvider>
        <Toaster position="top-right" richColors />
        <ZoneSelectionModal />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/invitation/:token" element={<InvitationPage />} />
          
          {/* Route Recherche */}
          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <SearchPage />
              </ProtectedRoute>
            }
          />
          {/* Redirect /dashboard/recherche → /search (alias) */}
          <Route path="/dashboard/recherche" element={<Navigate to="/search" replace />} />
          
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <RoleBasedDashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Routes fonctionnelles spécifiques (pas juste des blocs) */}
          <Route
            path="/dashboard/favoris"
            element={
              <ProtectedRoute>
                <FavorisPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/simulation"
            element={
              <ProtectedRoute>
                <SimulationCartPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/commandes/:id"
            element={
              <ProtectedRoute>
                <PurchaseOrderDetailPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/produit/:id"
            element={
              <ProtectedRoute>
                <ProductDetailPage />
              </ProtectedRoute>
            }
          />
          
          {/* ============================================ */}
          {/* ROUTE PREVIEW OFFRE (Sandbox Admin/Partner) */}
          {/* ============================================ */}
          <Route
            path="/dashboard/offer-preview/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'partenaire']}>
                <OfferPreviewSandbox />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/partner-preview/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'partenaire']}>
                <PartnerPreviewSandbox />
              </ProtectedRoute>
            }
          />
          
          {/* ============================================ */}
          {/* ROUTE PRINCIPALE: Mon Compte */}
          {/* ============================================ */}
          <Route
            path="/dashboard/profil"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          
          {/* Redirections vers la route principale */}
          <Route path="/profile" element={<Navigate to="/dashboard/profil" replace />} />
          <Route path="/dashboard/profile" element={<Navigate to="/dashboard/profil" replace />} />
          <Route path="/mon-compte" element={<Navigate to="/dashboard/profil" replace />} />
          
          <Route
            path="/dashboard/personal-space"
            element={
              <ProtectedRoute>
                <PersonalSpacePage />
              </ProtectedRoute>
            }
          />
          
          {/* Offer Detail Page - Route pour afficher une offre spécifique */}
          <Route
            path="/offre/:id"
            element={
              <ProtectedRoute>
                <OfferDetailPage />
              </ProtectedRoute>
            }
          />
          
          {/* Partner Routes */}
          <Route
            path="/dashboard/partner"
            element={
              <ProtectedRoute allowedRoles={['partenaire']}>
                <PartnerDashboard />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/partner/produits"
            element={
              <ProtectedRoute allowedRoles={['partenaire']}>
                <PartnerProducts />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/partner/offres"
            element={
              <ProtectedRoute allowedRoles={['partenaire']}>
                <PartnerOffers />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/partner/entreprise"
            element={
              <ProtectedRoute allowedRoles={['partenaire']}>
                <PartnerEntreprise />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/partner/commandes"
            element={
              <ProtectedRoute allowedRoles={['partenaire']}>
                <PartnerOrders />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/partner/commandes/:id"
            element={
              <ProtectedRoute allowedRoles={['partenaire']}>
                <PartnerOrderDetail />
              </ProtectedRoute>
            }
          />
          
          {/* P1.5+ — Partner claims system removed. Invitations managed by admin. */}

          
          {/* Admin Routes */}
          <Route
            path="/dashboard/admin"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/admin/produits"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminProducts />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/admin/publications"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminPublications />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/admin/partenaires"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminPartners />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/admin/organisations"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminOrganizations />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/admin/pages"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminPages />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/admin/pages/:pageId/blocks"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminPageBlocks />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/admin/global-blocks"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminGlobalBlocks />
              </ProtectedRoute>
            }
          />

          
          <Route
            path="/dashboard/admin/utilisateurs"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/admin/utilisateurs/:userId"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminEditUser />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/admin/audit"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminAudit />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/admin/offres"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminOffers />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/admin/avis"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminComments />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/admin/vitrines"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminVitrines />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/admin/import-v2"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminImportV2 />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/admin/invitations"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminInvitations />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/admin/referentiels"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminReferentiels />
              </ProtectedRoute>
            }
          />

          
          {/* ============================================ */}
          {/* 🏛️ ARCHITECTURE HUB + LISTING */}
          {/* ============================================ */}
          
          {/* 
            🟣 PAGES HUB (Marketing / Navigation)
            - Affichent bannière + navigation vers listings
            - Aucun appel API produits
            - Slugs: nouveautes, precommandes, offres
          */}
          <Route
            path="/dashboard/nouveautes"
            element={
              <ProtectedRoute>
                <HubPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/precommandes"
            element={
              <ProtectedRoute>
                <HubPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/offres"
            element={
              <ProtectedRoute>
                <HubPage />
              </ProtectedRoute>
            }
          />
          
          {/* 
            🟢 PAGES LISTING (Produits / Query Engine)
            - Affichent produits filtrés
            - Aucune bannière marketing
            - Slugs: nouveautes/en-cours, nouveautes/a-venir, etc.
          */}
          <Route
            path="/dashboard/nouveautes/:subPath"
            element={
              <ProtectedRoute>
                <ListingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/precommandes/:subPath"
            element={
              <ProtectedRoute>
                <ListingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/offres/:subPath"
            element={
              <ProtectedRoute>
                <ListingPage />
              </ProtectedRoute>
            }
          />
          {/* Sélection du Mois & Best-sellers (Phase 3.4.3) */}
          <Route
            path="/dashboard/selection-du-mois"
            element={
              <ProtectedRoute>
                <ListingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/bestsellers"
            element={
              <ProtectedRoute>
                <ListingPage />
              </ProtectedRoute>
            }
          />
          
          {/* ============================================ */}
          {/* 🔥 ROUTE DYNAMIQUE (fallback pour pages CMS) */}
          {/* ============================================ */}
          {/* IMPORTANT: Cette route doit être APRÈS les routes HUB/LISTING */}
          <Route
            path="/dashboard/:slug/*"
            element={
              <ProtectedRoute>
                <DynamicPage />
              </ProtectedRoute>
            }
          />
          

          {/* Partner Page - Route publique */}
          <Route path="/partner/:slug" element={<PartnerPage />} />
          
          {/* Partners List Page - Catalogue de tous les partenaires (Retailer) */}
          <Route 
            path="/partners" 
            element={
              <ProtectedRoute>
                <PartnersListPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Partner Catalog Page - Liste des produits d'un partenaire */}
          <Route 
            path="/partner/:slug/products" 
            element={
              <ProtectedRoute>
                <PartnerCatalogPage />
              </ProtectedRoute>
            } 
          />

          {/* Dynamic Public Pages */}
          <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/:slug" element={<PublicPage />} />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </CartProvider>
        </SignupModalProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
 App;
